import { FC, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    CommonChargingSetting,
    SmartChargingFieldName,
    SmartOrProgramChargingFields,
} from '@containers/emobility/charging-modes/shared/common-charging-prompt/types';
import { EmobilityChargingModeSettings } from '@containers/emobility';
import EmobilitySmartChargingPromptSetting from '@containers/emobility/charging-modes/shared/common-charging-prompt/setting';

import { useSetWallboxChargingMode } from '@store/actions';
import {
    useHasElectricCar,
    useTelemetries,
    useWallboxDeviceConfiguration,
    useWallboxSmartChargingData,
    useWallboxSmartChargingDefaultValues,
} from '@store/selectors';
import { GenericSmartChargingModelModeEnum } from '@swagger-http';
import { TelemetryTypes } from '@store/enums';

import './index.scss';
import { useSetEvProfile } from '../../hooks';

interface Props {
    onSave: () => void;
    onCancel: () => void;
}

export const EmobilitySmartChargingPrompt: FC<Readonly<Props>> = ({
    onSave,
    onCancel,
}: Props) => {
    const { t } = useTranslation();
    const hasElectricCar = useHasElectricCar();
    const {
        electricCar: { electricCarBatteryLevel: batteryLevel },
    } = useTelemetries(TelemetryTypes.EMOBILITY);

    const setSmartCharging = useSetWallboxChargingMode(
        GenericSmartChargingModelModeEnum.GridxSmartCharging,
    );

    const { setEvProfile, isElectricCarBatteryLevelMissing } =
        useSetEvProfile();

    const { defaultMinBattery } = useWallboxSmartChargingDefaultValues();

    const { active, departureTimestamp, minSoc, departureSoc } =
        useWallboxSmartChargingData();

    const wallboxDeviceConfiguration = useWallboxDeviceConfiguration();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid, isSubmitting },
        setValue,
    } = useForm<SmartOrProgramChargingFields>({
        mode: 'onChange',
        defaultValues: {
            departureTimestamp,
            batteryAtDeparture: departureSoc,
            minimumBattery: minSoc,
            currentBattery: Math.round(
                hasElectricCar
                    ? (batteryLevel ?? 0)
                    : (wallboxDeviceConfiguration?.userSoc ?? 0),
            ),
        },
    });

    const departureTime = watch('departureTimestamp');
    const batteryAtDeparture = watch('batteryAtDeparture');
    const minimumBattery = watch('minimumBattery');
    const currentBattery = watch('currentBattery');

    const settings: CommonChargingSetting[] = useMemo(
        () => [
            {
                title: t('Departure time:'),
                name: 'departureTimestamp',
                isTimePicker: true,
                value: departureTime,
                setValue: (val) =>
                    setValue('departureTimestamp', val as string),
            },
            {
                title: t('Battery level at departure:'),
                name: 'batteryAtDeparture',
                hint: '%',
                value: batteryAtDeparture,
                setValue: (val) =>
                    setValue('batteryAtDeparture', val as number),
                minValue: currentBattery,
            },
            {
                title: t('Minimum battery level:'),
                name: 'minimumBattery',
                hint: '%',
                value: minimumBattery,
                setValue: (val) => setValue('minimumBattery', val as number),
                minValue: defaultMinBattery,
            },
            {
                title: t('Current battery level:'),
                name: 'currentBattery',
                hint: '%',
                value: currentBattery,
                setValue: (val) => setValue('currentBattery', val as number),
                isReadOnly: hasElectricCar && !!batteryLevel,
            },
        ],
        // prettier-ignore
        [t, departureTime, batteryAtDeparture, currentBattery, minimumBattery, defaultMinBattery, hasElectricCar, batteryLevel, setValue],
    );

    const onSubmit = async (
        data: Record<SmartChargingFieldName, string | number>,
    ) => {
        await setEvProfile();

        setSmartCharging(
            {
                active: true,
                departureTimestamp: data.departureTimestamp as string,
                departureSoc: data.batteryAtDeparture as number,
                minSoc: data.minimumBattery as number,
                userSoc: data.currentBattery as number,
            },
            onSave,
        );
    };

    useEffect(() => {
        if (hasElectricCar && !!batteryLevel && !currentBattery) {
            setValue('currentBattery', batteryLevel);
        }
    }, [batteryLevel, currentBattery, hasElectricCar, setValue]);

    return (
        <EmobilityChargingModeSettings
            title={t('Set up your smart charging mode')}
            onSave={handleSubmit(
                onSubmit as SubmitHandler<SmartOrProgramChargingFields>,
            )}
            isActive={active}
            onCancel={onCancel}
            isSaveButtonDisabled={
                !isValid || isSubmitting || isElectricCarBatteryLevelMissing
            }
            infoboxContent={
                <>
                    <p>
                        {t(
                            'We optimize your charging based on your departure time and desired battery level, leveraging as much as possible on the solar surplus energy available.',
                        )}
                    </p>
                    <p>
                        {t(
                            'Once the desired battery level is reached, we will continue charging with the available solar surplus while the electric vehicle is plugged in.',
                        )}
                    </p>
                </>
            }
        >
            <div className="c-smart-charging">
                <div className="c-smart-charging__settings-container">
                    {settings.map(
                        ({
                            title,
                            name,
                            hint,
                            isTimePicker,
                            value,
                            setValue,
                            minValue,
                            isReadOnly,
                        }) => (
                            <div
                                key={title}
                                className={`c-smart-charging__setting ${
                                    isReadOnly
                                        ? 'c-smart-charging__setting--readonly'
                                        : ''
                                }`}
                            >
                                <EmobilitySmartChargingPromptSetting
                                    name={name}
                                    hint={hint}
                                    title={title}
                                    register={register}
                                    isTimePicker={isTimePicker}
                                    value={value}
                                    setValue={setValue}
                                    errors={errors}
                                    minValue={minValue}
                                    isReadOnly={isReadOnly}
                                />
                            </div>
                        ),
                    )}
                </div>
            </div>
        </EmobilityChargingModeSettings>
    );
};

export default EmobilitySmartChargingPrompt;
